import { memo, useCallback } from 'react';

import { usePDFSearchBridgeContext } from '~/components/AssetModalV2/Content/Header/contexts/PDFSearchBridgeContextProvider';
import { PDFSearch } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PDFSearch/PDFSearch';

/**
 * Connects the PDF search bridge context to the presentational {@link PDFSearch}. Placement is
 * decided by the consumer: the desktop asset modal renders it in the content header, while the
 * mobile PDF visualizer renders it as an overlay. The bridge wiring is identical either way.
 */
export const ConnectedPDFSearch = memo(() => {
  const {
    initialSearchPhrase,
    searchState,
    setCurrentResult,
    setInitialSearchPhrase,
    triggerClearSearch,
    triggerSearch,
  } = usePDFSearchBridgeContext();

  const handlePDFSearchChange = useCallback(
    (phrase: string) => {
      if (phrase.trim()) {
        triggerSearch(phrase);
      } else {
        triggerClearSearch();
      }
    },
    [triggerSearch, triggerClearSearch],
  );

  const handlePDFSearchClear = useCallback(() => {
    setInitialSearchPhrase(null);
    triggerClearSearch();
  }, [setInitialSearchPhrase, triggerClearSearch]);

  return (
    <PDFSearch
      initialSearchPhrase={initialSearchPhrase}
      isSearching={searchState.isSearching}
      currentResult={searchState.currentResultIndex}
      totalResults={searchState.totalResults}
      onSearchChange={handlePDFSearchChange}
      clearSearch={handlePDFSearchClear}
      setCurrentResultIndex={setCurrentResult}
    />
  );
});

ConnectedPDFSearch.displayName = 'ConnectedPDFSearch';
